import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {SegmentSearchFilterModel} from './search-filter.model';
import {Observable, of} from 'rxjs';
import {SubSink} from 'subsink';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {TargetFacadeService} from '../../../target/target-facade.service';
import {DistributionAccountSearchModel} from '../../../distribution-account/distribution-account.model';
import {SegmentListConfig} from '../../segment-config.model';
import {Taxonomy} from '../../../taxonomy/taxonomy.reducer';
import {TaxonomyFacadeService} from '../../../taxonomy/taxonomy-facade.service';
import {flatten} from '../../segments.state';
import {Store} from '@ngrx/store';
import {
  selectCategories,
  selectSegmentAudienceTypes,
  selectSegmentMainTypes,
  selectSegmentSubTypes
} from '../../../filters-config/filters-config.selectors';

@Component({
  selector: 'app-segment-search',
  template: `
    <div [formGroup]="segmentSearchForm" class="segment-search-input-container">
      <div class="search-field-wrapper">
        <div class="left-column">
          <mat-form-field id="input-segment-name" *ngIf="config.filtersToPresent.includes('name')">
            <input formControlName="name"
                   matInput
                   autocomplete="off"
                   name="input-segment-name"
                   placeholder="Segment Name"/>
          </mat-form-field>
          <mat-form-field *ngIf="config.filtersToPresent.includes('segmentIds')">
            <input formControlName="segmentIds" matInput name="input-segment-segmentId"
                   placeholder="Segment IDs"/>
          </mat-form-field>
          <mat-form-field *ngIf="config.filtersToPresent.includes('clientSideId')">
            <input formControlName="clientSideId" matInput name="input-segment-clientSideId"
                   placeholder="Client Side eID"/>
          </mat-form-field>
          <mat-form-field *ngIf="config.filtersToPresent.includes('category')">
            <mat-label>Category</mat-label>
            <mat-select formControlName="category" multiple>
              <mat-option *ngFor="let label of categories | async" [value]="label">{{ label }}</mat-option>
            </mat-select>
          </mat-form-field>
          <div *ngIf="config.filtersToPresent.includes('applyFilterToChildren')"
               class="apply-filter-to-children-checkbox">
            <mat-checkbox formControlName="applyFilterToChildren"
                          [matTooltip]="'Check to apply filters to children. ' +
                           'If not checked then children will be fetched and attached to its parent regardless of filter'">
              Apply filter to children
            </mat-checkbox>
          </div>
        </div>
        <div class="right-column">
          <mat-form-field *ngIf="config.filtersToPresent.includes('mainType')">
            <mat-label>Main Type</mat-label>
            <mat-select formControlName="segmentMainTypes" multiple>
              <mat-option *ngFor="let label of segmentMainTypes | async" [value]="label">{{ label }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field *ngIf="config.filtersToPresent.includes('subType')">
            <mat-label>Sub Type</mat-label>
            <mat-select formControlName="segmentSubTypes" multiple>
              <mat-option *ngFor="let label of segmentSubTypes | async" [value]="label">{{ label }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field *ngIf="config.filtersToPresent.includes('audienceType')">
            <mat-label>Audience Type</mat-label>
            <mat-select formControlName="segmentAudienceTypes" multiple>
              <mat-option *ngFor="let label of segmentAudienceTypes | async" [value]="label">{{ label }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field *ngIf="config.filtersToPresent.includes('accounts')">
            <mat-label>Shared accounts</mat-label>
            <mat-select formControlName="accounts"
                        data-cy="select-segment-filter-accounts"
                        multiple>
              <mat-option *ngFor="let account of accountsSource$ | async"
                          [value]="account"
                          data-name="dist-account-option">
                {{ account.description }}
              </mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field *ngIf="config.filtersToPresent.includes('ttdCompliance')">
            <mat-label>TTD Compliance</mat-label>
            <mat-select formControlName="ttdCompliance" multiple>
              <mat-option *ngFor="let label of ttdComplianceValues | async" [value]="label">{{ label }}</mat-option>
            </mat-select>
          </mat-form-field>


          <mat-form-field *ngIf="config.filtersToPresent.includes('taxonomies')">
            <mat-label>Taxonomy</mat-label>
            <mat-select formControlName="taxonomies"
                        data-cy="select-segment-filter-taxonomy"
                        multiple>
              <mat-option *ngFor="let taxonomy of taxonomySource$ | async"
                          [value]="taxonomy"
                          [ngStyle]="{paddingLeft: taxonomy.level? taxonomy.level*17 + 'px' : '5px'}"
                          data-name="dist-taxonomy-option">
                ({{ taxonomy.id }}) {{ taxonomy.name }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <button [disabled]="!showSearchFormClearButton()"
                id="btn-clear-search"
                color="primary"
                class="clear-search-form-button"
                mat-raised-button
                (click)="clearSearch()">Clear
        </button>
      </div>
    </div>
  `,
  styleUrls: ['search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SegmentSearchComponent implements OnDestroy {
  @Output() searchForm = new EventEmitter<SegmentSearchFilterModel>();
  categories: Observable<string[]> = this.store.select(selectCategories).pipe(map(results => results.sort()));
  segmentMainTypes: Observable<string[]> = this.store.select(selectSegmentMainTypes).pipe(map(results => results.sort()));
  segmentSubTypes: Observable<string[]> = this.store.select(selectSegmentSubTypes).pipe(map(results => results.sort()));
  segmentAudienceTypes: Observable<string[]> = this.store.select(selectSegmentAudienceTypes).pipe(map(results => results.sort()));
  ttdComplianceValues: Observable<string[]> = of(['NotInQueue', 'Pending', 'Approved', 'Denied']);
  accountsSource$: Observable<DistributionAccountSearchModel[]> = this.targetFacade.getAccountsFromTargets()
    .pipe(
      map(it => it.map(acc => ({
        id: acc.id,
        description: acc.description
      })).sort((a, b) =>
        a.description.toUpperCase().localeCompare(b.description.toUpperCase()))));
  taxonomySource$: Observable<Taxonomy[]> = this.taxonomyFacade.getAll().pipe(map(it => flatten(it)));
  segmentSearchForm = new FormGroup({
    segmentIds: new FormControl(),
    name: new FormControl(),
    partnerId: new FormControl(),
    clientSideId: new FormControl(),
    segmentMainTypes: new FormControl(),
    segmentSubTypes: new FormControl(),
    segmentAudienceTypes: new FormControl(),
    category: new FormControl(),
    accounts: new FormControl(),
    state: new FormControl(),
    applyFilterToChildren: new FormControl(),
    taxonomies: new FormControl(),
    ttdCompliance: new FormControl()
  });
  subsink = new SubSink();

  constructor(
    private targetFacade: TargetFacadeService,
    private store: Store,
    private taxonomyFacade: TaxonomyFacadeService
  ) {
    this.subsink.sink = this.segmentSearchForm.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe((value) => {
      this.searchForm.emit(
        {
          ...value,
          segmentIds: value?.segmentIds ? value.segmentIds.split(' ') : []
        } as SegmentSearchFilterModel);
    });
  }

  private _config!: SegmentListConfig;

  get config(): SegmentListConfig {
    return this._config;
  }

  @Input() set config(value: SegmentListConfig) {
    this._config = value;
    setTimeout(_ => {
      this.segmentSearchForm.patchValue(this._config.defaultFilterValues, {emitEvent: true});
    });
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  clearSearch() {
    this.segmentSearchForm.patchValue(this._config.defaultFilterValues);
  }

  showSearchFormClearButton() {
    return Object.keys(this.segmentSearchForm.value)
      .some(formControlKey => {
        if (Array.isArray(this.segmentSearchForm.value[formControlKey])) {
          return this.segmentSearchForm.value[formControlKey].length > 0;
        }
        return !!this.segmentSearchForm.value[formControlKey];
      });
  }
}
